import React, { useState } from "react";
import { IClub } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { localStorageService } from "src/services/local-storage.service";
import { toast } from "src/components/Toast/ToastManager";
import { Modal } from "src/components/Modal/Modal";
import Button from "src/components/Button/Button";
import Checkbox from "src/components/Checkbox/Checkbox";
import Icon from "src/components/Icon/Icon";

import styles from "./FavoriteClubModal.module.scss";

interface IProps {
    isShown: boolean;
    hide: () => void;
    club: IClub | null;
    onComplete?: () => void;
}

const FavoriteClubModal: React.FC<IProps> = ({ isShown, hide, club, onComplete }) => {
    const [optIn, setOptIn] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const handleDone = async () => {
        if (!club) return;
        
        setIsSubmitting(true);
        try {
            if (optIn) {
                // Opt in to club communications
                const updatedUser = await apiService.optInToClub(club._id);
                localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
                
                toast.show({
                    title: "Club Communications",
                    content: `You're now subscribed to communications from ${club.club_name}`,
                    duration: 3000,
                    type: "success"
                });
            }
            
            // Reset and close
            setOptIn(false);
            hide();
            if (onComplete) {
                onComplete();
            }
        } catch (error) {
            toast.show({
                title: "Error",
                content: "Unable to update communication preferences.",
                duration: 5000,
                type: "fail"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setOptIn(false);
        hide();
        if (onComplete) {
            onComplete();
        }
    };

    if (!club) return null;

    return (
        <Modal
            isShown={isShown}
            hide={handleClose}
            className={styles.FavoriteClubModal}
            modalContent={
                <div className={styles.content}>
                    <div className={styles.header}>
                        <Icon type="heartFilled" className={styles.heartIcon} />
                        <h2>{club.club_name} Added to Favorites</h2>
                    </div>
                    
                    <div className={styles.body}>
                        <p className={styles.description}>
                            As a favorite, you'll receive automated reminders when {club.club_name} creates new events, courses, and classes.
                        </p>
                        
                        <div className={styles.divider} />
                        
                        <p className={styles.question}>
                            Would you also like to receive direct communications from this club about club specific promotions and announcements?
                        </p>
                        
                        <div className={styles.optInCheckbox}>
                            <Checkbox
                                id="favorite-club-opt-in"
                                label={`Yes, I'd like to hear from ${club.club_name}`}
                                value={optIn}
                                onChange={(e) => setOptIn(e.target.checked)}
                            />
                        </div>
                    </div>
                    
                    <div className={styles.footer}>
                        <Button 
                            onClick={handleDone}
                            isLoading={isSubmitting}
                        >
                            Done
                        </Button>
                    </div>
                </div>
            }
        />
    );
};

export default FavoriteClubModal;

